import React from 'react';

export interface StatusFilterOption<S extends string> {
  key: S;
  label: string;
}

interface Props<S extends string> {
  options: StatusFilterOption<S>[];
  active: S;
  onChange: (key: S) => void;
  counts: Partial<Record<S, number>>;
  countsLoading?: boolean;
}

/**
 * Clickable status tabs with per-status counts (e.g. "Vacant (10)"), driving a
 * server-side status filter. Include an `'all'` option in `options`/`counts` for
 * the unfiltered total — see useStatusCounts, which fetches every key (including
 * 'all') via a real query rather than summing, since status sets aren't always
 * exhaustive (e.g. visitors have a PRE_APPROVED state outside the visible tabs).
 */
function StatusFilterBar<S extends string>({
  options, active, onChange, counts, countsLoading,
}: Props<S>) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
      {options.map(opt => {
        const isActive = active === opt.key;
        const count = counts[opt.key];
        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt.key)}
            style={{
              fontSize: 12, padding: '4px 12px', borderRadius: 99, cursor: 'pointer',
              background: isActive ? 'var(--nx-primary-accent)' : 'transparent',
              color: isActive ? '#fff' : 'var(--nx-text-secondary)',
              border: `1px solid ${isActive ? 'var(--nx-primary-accent)' : 'var(--nx-border)'}`,
              fontWeight: isActive ? 600 : 400,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            {opt.label}
            <span style={{ opacity: 0.75 }}>
              {countsLoading && count === undefined ? '(…)' : `(${count ?? 0})`}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default StatusFilterBar;
